import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import LinearProgress from "@material-ui/core/LinearProgress";
import Box from "@material-ui/core/Box";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  chip: {
    minWidth: 100,
  },
  progressBar: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
}));

const ButtonsListsCampaigns = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, [searchParam, typeFilter, statusFilter]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns/buttons-lists", {
        params: {
          searchParam,
          type: typeFilter,
          status: statusFilter,
        },
      });
      setCampaigns(data.campaigns || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/buttons-lists/${campaignId}`);
      toast.success("Campaña eliminada exitosamente");
      loadCampaigns();
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
  };

  const handleStartCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns/buttons-lists/${campaignId}/start`);
      toast.success("Campaña iniciada");
      loadCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns/buttons-lists/${campaignId}/pause`);
      toast.success("Campaña pausada");
      loadCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      scheduled: "primary",
      running: "secondary",
      paused: "default",
      completed: "primary",
      cancelled: "default",
    };
    return colors[status] || "default";
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: "Borrador",
      scheduled: "Programada",
      running: "En ejecución",
      paused: "Pausada",
      completed: "Completada",
      cancelled: "Cancelada",
    };
    return labels[status] || status;
  };

  const getTypeLabel = (type) => {
    return type === "buttons" ? "Botones" : "Lista";
  };

  const calculateProgress = (campaign) => {
    if (campaign.totalRecipients === 0) return 0;
    return Math.round(
      ((campaign.sentCount + campaign.failedCount) / campaign.totalRecipients) *
        100
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Eliminar Campaña"
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign)}
      >
        ¿Está seguro que desea eliminar esta campaña?
      </ConfirmationModal>
      <MainHeader>
        <Title>Campañas de Botones y Listas</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.push("/campaigns/buttons-lists/create")}
            startIcon={<AddIcon />}
          >
            Nueva Campaña
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="Buscar campaña"
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="buttons">Botones</MenuItem>
                <MenuItem value="list">Lista</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="draft">Borrador</MenuItem>
                <MenuItem value="scheduled">Programada</MenuItem>
                <MenuItem value="running">En ejecución</MenuItem>
                <MenuItem value="paused">Pausada</MenuItem>
                <MenuItem value="completed">Completada</MenuItem>
                <MenuItem value="cancelled">Cancelada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Nombre</TableCell>
              <TableCell align="center">Tipo</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Progreso</TableCell>
              <TableCell align="center">Destinatarios</TableCell>
              <TableCell align="center">Enviados</TableCell>
              <TableCell align="center">Fallidos</TableCell>
              <TableCell align="center">Fecha Creación</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {loading ? (
                <TableRowSkeleton columns={9} />
              ) : (
                <>
                  {campaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No hay campañas creadas
                      </TableCell>
                    </TableRow>
                  ) : (
                    campaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell align="center">{campaign.name}</TableCell>
                        <TableCell align="center">
                          {getTypeLabel(campaign.type)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getStatusLabel(campaign.status)}
                            color={getStatusColor(campaign.status)}
                            size="small"
                            className={classes.chip}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center">
                            <Box width="100%" mr={1}>
                              <LinearProgress
                                variant="determinate"
                                value={calculateProgress(campaign)}
                                className={classes.progressBar}
                              />
                            </Box>
                            <Box minWidth={35}>
                              <span>{calculateProgress(campaign)}%</span>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {campaign.totalRecipients}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.sentCount}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.failedCount}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.createdAt
                            ? format(
                                parseISO(campaign.createdAt),
                                "dd/MM/yyyy HH:mm",
                                { locale: es }
                              )
                            : "-"}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver detalles">
                            <IconButton
                              size="small"
                              onClick={() =>
                                history.push(
                                  `/campaigns/buttons-lists/${campaign.id}`
                                )
                              }
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>

                          {campaign.status === "draft" && (
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  history.push(
                                    `/campaigns/buttons-lists/${campaign.id}/edit`
                                  )
                                }
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          {(campaign.status === "draft" ||
                            campaign.status === "paused" ||
                            campaign.status === "scheduled") && (
                            <Tooltip title="Iniciar">
                              <IconButton
                                size="small"
                                onClick={() => handleStartCampaign(campaign.id)}
                              >
                                <PlayArrowIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          {campaign.status === "running" && (
                            <Tooltip title="Pausar">
                              <IconButton
                                size="small"
                                onClick={() => handlePauseCampaign(campaign.id)}
                              >
                                <PauseIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setDeletingCampaign(campaign.id);
                                setConfirmModalOpen(true);
                              }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </>
              )}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ButtonsListsCampaigns;
